import { useEffect, useRef } from "react";

/**
 * Fond animé de l'en-tête de la page Outils : un graphique en chandeliers discret
 * qui défile lentement vers la gauche. Hausse = vert, baisse = orange (palette Side-B).
 * - s'estompe vers la gauche pour ne pas gêner le titre (masque CSS)
 * - image fixe si l'utilisateur a demandé moins d'animations (prefers-reduced-motion)
 * - s'arrête automatiquement quand l'onglet n'est pas visible (requestAnimationFrame)
 * Le parent doit être en `position: relative`.
 */
const GREEN = "0,191,99";
const ORANGE = "247,152,55";
const STEP = 14; // largeur d'une bougie + espace (px)
const SPEED = 10; // vitesse de défilement (px / seconde)

export default function CandlesBackground({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let candles = [];
    let shift = 0;
    let last = 0;
    let frame = 0;

    const makeCandle = (open) => {
      const close = open + (Math.random() - 0.48) * height * 0.16 - (open - height * 0.5) * 0.06;
      const high = Math.min(open, close) - Math.random() * height * 0.05;
      const low = Math.max(open, close) + Math.random() * height * 0.05;
      return { open, close, high, low };
    };

    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.ceil(width / STEP) + 3;
      let price = height * 0.5;
      candles = [];
      for (let i = 0; i < count; i += 1) {
        const c = makeCandle(price);
        candles.push(c);
        price = c.close;
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      candles.forEach((c, i) => {
        const x = i * STEP - shift;
        const up = c.close < c.open; // y vers le bas : close plus haut = hausse
        const color = up ? GREEN : ORANGE;
        ctx.strokeStyle = `rgba(${color},0.28)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + 4, c.high);
        ctx.lineTo(x + 4, c.low);
        ctx.stroke();
        ctx.fillStyle = `rgba(${color},${up ? 0.22 : 0.16})`;
        ctx.fillRect(x + 1, Math.min(c.open, c.close), 7, Math.max(1.5, Math.abs(c.close - c.open)));
      });
    };

    const tick = (now) => {
      const dt = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;
      shift += SPEED * dt;
      while (shift >= STEP) {
        shift -= STEP;
        candles.shift();
        candles.push(makeCandle(candles[candles.length - 1].close));
      }
      draw();
      frame = requestAnimationFrame(tick);
    };

    setup();
    draw();
    if (!reduceMotion) frame = requestAnimationFrame(tick);

    const observer = new ResizeObserver(() => {
      setup();
      draw();
    });
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const mask = "linear-gradient(90deg, transparent 0%, transparent 45%, #000 75%)";
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`hero-anim absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
      data-testid="tools-candles-bg"
    />
  );
}
